import { memo, useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Camera, Link as LinkIcon, FileText, Image as ImageIcon, Clock, Star, Mic, Users, CheckCircle2, ArrowRight } from "lucide-react";
import type { BountyRow } from "@/types";
import { isExpired, getActualBountyStatus, stripHtml } from "@/lib/utils";
import { formatTimeRemaining } from "@/lib/serverTime";
import { ROUTES } from "@/config/constants";
import { FeaturedBadge } from "@/components/ProBadge";
import { supabase } from "@/integrations/supabase/client";
import { countryToFlag } from "@/lib/countryFlags";

const proofIcon = {
  photo: Camera,
  screenshot: ImageIcon,
  link: LinkIcon,
  text: FileText,
  video: ImageIcon,
  document: FileText,
  audio: Mic,
};

export const BountyCard = memo(({ bounty, ...props }: { bounty: BountyRow }) => {
  const Icon = proofIcon[bounty.proof_type];
  const reward = (bounty.reward_cents / 100).toFixed(2);
  const actualStatus = getActualBountyStatus(bounty.status, bounty.expires_at);
  const expired = actualStatus === 'expired';
  // Featured status is only active if: is_featured=true AND featured_expires_at exists AND hasn't expired yet
  // AND the bounty itself is not expired
  const featuredNotExpired = bounty.featured_expires_at && !isExpired(bounty.featured_expires_at);
  const isFeatured = bounty.is_featured && featuredNotExpired && !expired;
  
  // Track claim status for visibility on bounty board
  const [approvedCount, setApprovedCount] = useState(0);
  const [acceptedCount, setAcceptedCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [hasSubmittedProof, setHasSubmittedProof] = useState(false);
  const [hasApprovedProofs, setHasApprovedProofs] = useState(false);
  const [maxClaimers, setMaxClaimers] = useState(bounty.max_claimers || 1);
  const [isFullyClaimed, setIsFullyClaimed] = useState(false);
  const countryFlag = bounty.country ? countryToFlag(bounty.country) : '';
  
  useEffect(() => {
    const loadClaimCount = async () => {
      if (bounty.status === 'claimed' || bounty.status === 'submitted' || bounty.status === 'completed') {
        // Use RPC instead of direct query to bypass RLS restrictions
        // (get_bounty_claims is SECURITY DEFINER and works for any authenticated user)
        const { data, error } = await supabase.rpc('get_bounty_claims', {
          p_bounty_id: bounty.id
        });
        
        if (!error && data) {
          const approved = data.filter(c => c.proof_status === 'approved').length;
          const accepted = data.filter(c => c.status === 'accepted').length;
          const pending = data.filter(c => c.status === 'pending').length;
          const approvedProofExists = data.some(c => c.proof_status === 'approved');
          const submittedProof = data.some(c => c.status === 'accepted' && c.proof_status === 'pending');
          
          setApprovedCount(approved);
          setAcceptedCount(accepted);
          setPendingCount(pending);
          setHasSubmittedProof(submittedProof);
          setHasApprovedProofs(approvedProofExists);
          
          const maxC = bounty.max_claimers || 1;
          setMaxClaimers(maxC);
          setIsFullyClaimed(approved >= maxC);
        } else {
          // Fallback: direct query for environments where RPC is unavailable
          const { data: fallbackData } = await supabase
            .from('claim_requests')
            .select('id, proof_status, status')
            .eq('bounty_id', bounty.id);
          
          if (fallbackData) {
            const approved = fallbackData.filter(c => c.proof_status === 'approved').length;
            const accepted = fallbackData.filter(c => c.status === 'accepted').length;
            const pending = fallbackData.filter(c => c.status === 'pending').length;
            const approvedProofExists = fallbackData.some(c => c.proof_status === 'approved');
            const submittedProof = fallbackData.some(c => c.status === 'accepted' && c.proof_status === 'pending');
            
            setApprovedCount(approved);
            setAcceptedCount(accepted);
            setPendingCount(pending);
            setHasSubmittedProof(submittedProof);
            setHasApprovedProofs(approvedProofExists);
            
            const maxC = bounty.max_claimers || 1;
            setMaxClaimers(maxC);
            setIsFullyClaimed(approved >= maxC);
          }
        }
      }
    };
    
    loadClaimCount();
  }, [bounty.id, bounty.status, bounty.max_claimers]);

  const statusLabel = () => {
    if (expired) return "Expired";
    // For fully completed bounties (all proofs approved), show expired instead of countdown
    if (isFullyClaimed && hasApprovedProofs) return "Completed";
    if (hasApprovedProofs && maxClaimers > 1) return `${approvedCount}/${maxClaimers} Claimed`;
    if (isFullyClaimed) return 'Fully Claimed';
    if (actualStatus === 'submitted' && hasSubmittedProof) return 'Proof Submitted';
    if (actualStatus === 'claimed' && maxClaimers > 1) return `Awaiting Proof${acceptedCount > 0 ? ` (${acceptedCount}/${maxClaimers})` : ''}`;
    if (actualStatus === 'claimed' && maxClaimers === 1) return 'Awaiting Proof';
    if (actualStatus === 'submitted') return 'Proof Submitted';
    return actualStatus;
  };

  return (
    <Link to={ROUTES.BOUNTY_DETAIL(bounty.id)} className="block group h-full" {...props}>
      <motion.article
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        whileHover={{ y: -4 }}
        whileTap={{ scale: 0.99 }}
        transition={{ type: "spring", stiffness: 300, damping: 24 }}
        className={`
          relative flex h-full flex-col rounded-2xl border bg-card p-5 shadow-sm
          transition-shadow duration-300
          ${isFeatured
            ? 'border-[hsl(38_92%_50%)] ring-1 ring-[hsl(38_92%_50%)/40]'
            : 'border-border group-hover:border-[hsl(38_92%_50%)/60] group-hover:shadow-lg'}
        `}
        style={isFeatured ? {
          boxShadow: "0 12px 32px -12px hsl(38 92% 50% / 0.35), 0 4px 12px -4px hsl(24 20% 10% / 0.12)",
        } : undefined}
      >
        {/* Featured badge for Pro bounties */}
        {isFeatured && (
          <div className="absolute -top-3 left-4">
            <FeaturedBadge size="sm" />
          </div>
        )}
        
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground capitalize">
              <Icon className="h-3.5 w-3.5 text-[hsl(38_80%_42%)]" /> {bounty.proof_type}
            </span>
            {bounty.country && (
              <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground">
                {countryToFlag(bounty.country)} {bounty.country}
              </span>
            )}
          </div>
          {!expired && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground shrink-0 pt-1">
              <Clock className="h-3.5 w-3.5" />
              {formatTimeRemaining(bounty.expires_at)}
            </span>
          )}
        </div>
        <h3 className="font-display text-lg font-bold leading-snug mb-2 line-clamp-2">{bounty.title}</h3>
        
        {/* Description - Compact list format */}
        <div className="text-sm text-muted-foreground mb-4 max-h-24 overflow-hidden relative">
          {useMemo(() => {
            const plainText = stripHtml(bounty.description);
            const lines = plainText.split(/\r?\n/).filter(line => line.trim());
            const hasNumberedList = lines.some(line => /^\d+\.?\s+/.test(line.trim()));
            
            if (hasNumberedList) {
              return (
                <div className="space-y-1">
                  {lines.slice(0, 3).map((line, index) => {
                    const trimmed = line.trim();
                    const listMatch = trimmed.match(/^(\d+)\.?\s+(.+)$/);
                    if (listMatch) {
                      return (
                        <div key={index} className="flex items-center gap-1.5">
                          <span className="flex-shrink-0 w-4 h-4 rounded-full bg-primary/15 text-primary text-[9px] font-bold flex items-center justify-center">
                            {listMatch[1]}
                          </span>
                          <span className="truncate text-xs">{listMatch[2]}</span>
                        </div>
                      );
                    }
                    return <p key={index} className="truncate text-xs">{trimmed}</p>;
                  })}
                </div>
              );
            }
            return <p className="line-clamp-3 leading-relaxed">{plainText}</p>;
          }, [bounty.description])}
          
          {/* Fade overlay for long content */}
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[hsl(var(--card))] to-transparent pointer-events-none" />
        </div>
        
        {/* Reference Photos Indicator */}
        {(bounty as any).reference_photos?.length > 0 && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3">
            <ImageIcon className="h-3 w-3" />
            <span>{(bounty as any).reference_photos.length} reference photo{(bounty as any).reference_photos.length > 1 ? 's' : ''}</span>
          </div>
        )}

        {/* Claim status info bar for non-open bounties (visibility for all users) */}
        {actualStatus !== "open" && !expired && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground mb-3">
            {hasApprovedProofs && maxClaimers > 1 && (
              <span className="flex items-center gap-1 text-emerald-600">
                <CheckCircle2 className="h-3 w-3" /> {approvedCount}/{maxClaimers} claimed
              </span>
            )}
            {hasApprovedProofs && maxClaimers === 1 && (
              <span className="flex items-center gap-1 text-emerald-600 font-semibold">
                <CheckCircle2 className="h-3 w-3" /> Reward paid
              </span>
            )}
            {!hasApprovedProofs && isFullyClaimed && (
              <span className="flex items-center gap-1 text-emerald-600">
                <Users className="h-3 w-3" /> All slots filled
              </span>
            )}
            {!hasApprovedProofs && !isFullyClaimed && acceptedCount > 0 && (
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" /> {acceptedCount} hunter{acceptedCount !== 1 ? 's' : ''} accepted
              </span>
            )}
            {!hasApprovedProofs && hasSubmittedProof && !isFullyClaimed && (
              <span className="flex items-center gap-1 text-blue-600">
                Proof submitted
              </span>
            )}
            {!hasApprovedProofs && pendingCount > 0 && !isFullyClaimed && (
              <span className="flex items-center gap-1 text-amber-600">
                {pendingCount} pending claim{pendingCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground border-t border-border pt-3">
          {bounty.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {bounty.location}</span>}
        </div>

        <div className="mt-3 flex items-center justify-between gap-3">
          <span className="reward-badge text-sm">${reward}</span>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
            {statusLabel() !== 'open' && statusLabel() !== 'Open' ? (
              <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{statusLabel()}</span>
            ) : (
              <span className="inline-flex items-center gap-1">
                View task <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            )}
          </span>
        </div>
        
        {/* Gold accent line for featured bounties */}
        {isFeatured && (
          <div className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl bg-gradient-to-r from-[hsl(45_95%_55%)] to-[hsl(35_88%_46%)]" />
        )}
      </motion.article>
    </Link>
  );
});

BountyCard.displayName = "BountyCard";
